import { Fragment, useState, useEffect } from "react";
import { Dialog, Disclosure, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Button from "../button";
import { useLazyGetProfessionServicesQuery } from "../../store/professions";
import Loader from "../commons/Loader";
import CreateOptions from "../commons/createOptions";
import { useAssignProfessionalServicesMutation } from "../../store/admin/professionalUserInfo";
import { toast } from "react-toastify";

const AdminProfessionServices = ({
  showModal,
  setShowModal,
  previousModal,
  selectedProfession,
  professionalInfo,
}) => {
  const [results, setResults] = useState([]);
  const [selectedProfessionServices, setSelectedProfessionServices] =
    useState();

  const [getProfessionServices] = useLazyGetProfessionServicesQuery();

  const [
    saveProfessionalServices,
    {
      data: saveProfessionalServicesData,
      isSuccess: saveProfessionalServicesSuccess,
      isLoading: saveProfessionalServicesLoader,
    },
  ] = useAssignProfessionalServicesMutation();

  useEffect(() => {
    if (saveProfessionalServicesSuccess && !saveProfessionalServicesLoader) {
      toast.success(saveProfessionalServicesData.message);
      setShowModal(false);
    }
  }, [saveProfessionalServicesSuccess, saveProfessionalServicesLoader]);

  useEffect(() => {
    if (professionalInfo?.professions) {
      let tempObject = { ...selectedProfessionServices };
      professionalInfo?.professions.forEach((profession) => {
        tempObject = {
          ...tempObject,
          [profession.id]: profession.services,
        };
      });
      setSelectedProfessionServices(tempObject);
    }
  }, [professionalInfo?.professions, results]);

  useEffect(() => {
    if (selectedProfession) {
      const fetchServices = async () => {
        const promises = selectedProfession.map((profession) =>
          getProfessionServices(profession.id)
        );

        const responses = await Promise.all(promises);
        const data = responses.map((response) => response.data);
        setResults(data);
      };

      fetchServices();
    }
  }, [selectedProfession]);

  const formatServices = (professionServicesData) => {
    return (
      professionServicesData &&
      professionServicesData.length > 0 &&
      professionServicesData.map((service) => {
        return { id: service.id, text: service.name };
      })
    );
  };

  const handleCheckbox = (service, isChecked) => {
    // Create a copy of the current selected services state
    let tempObject = { ...selectedProfessionServices };

    // Retrieve the list of services for the given profession and make a copy of it
    let professionServices = tempObject[service.profession_id]
      ? [...tempObject[service.profession_id]]
      : [];

    // Find the index of the service in the list
    let serviceIndex = professionServices.findIndex(
      (selService) => selService.id === service.id
    );

    if (isChecked) {
      // Add the service if isChecked is true and it doesn't exist
      if (serviceIndex === -1) {
        professionServices.push(service);
      }
    } else {
      // Remove the service if isChecked is false and it exists
      if (serviceIndex !== -1) {
        professionServices.splice(serviceIndex, 1);
      }
    }

    // Remove the profession if no services left, otherwise update the list
    if (professionServices.length === 0) {
      delete tempObject[service.profession_id];
    } else {
      tempObject[service.profession_id] = professionServices;
    }

    // Update the state with the modified services list
    setSelectedProfessionServices(tempObject);
  };

  const handleBackButtonClick = () => {
    setShowModal(false);
    previousModal(true);
  };

  const handleSaveProfessionalServices = () => {
    console.log(
      selectedProfessionServices,
      "inside save button",
      professionalInfo.id
    );
    let service_ids = Object.values(selectedProfessionServices)
      .flat()
      .map((service) => service.id);
    const params = {
      professionalId: professionalInfo?.id,
      services: service_ids,
    };
    saveProfessionalServices(params);
  };

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setShowModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-[10px] bg-white p-6 text-left shadow-xl transition-all sm:my-8 w-full max-w-[710px]">
                <div className="mb-5 flex items-center justify-between gap-5 pb-2">
                  <Dialog.Title
                    as="h3"
                    className="text-lg md:text-[22px] leading-[160%] font-bold text-customGray"
                  >
                    Select Profession Services to Add:
                  </Dialog.Title>
                  <XMarkIcon
                    className="w-5 sm:w-6 h-5 sm:h-6 cursor-pointer hover:text-red-500"
                    aria-hidden="true"
                    onClick={() => setShowModal(false)}
                  />
                </div>
                {!results ? (
                  <Loader isLoading={true} />
                ) : (
                  <div className="max-w-[626px] max-h-[calc(100vh-300px)] scroller overflow-y-auto pr-1">
                    <div className="flex flex-col items-start gap-5 w-full">
                      {selectedProfession?.map((list, index) => (
                        <>
                          <Disclosure
                            as="div"
                            key={list.id}
                            className={`faqs-item bg-[#F1F4F6] rounded-[10px] py-3 px-5 md:p-6 w-full overflow-hidden border-transparent`}
                            defaultOpen={index === 0 && true}
                          >
                            {({ open }) => (
                              <>
                                <Disclosure.Button
                                  as="dt"
                                  className="flex w-full items-start justify-between md:cursor-pointer text-left text-gray-900 outline-none focus:bg-transparent"
                                  onClick={() => {
                                    // setSelectedProfessionServices(list.id);
                                  }}
                                >
                                  <div className="flex md:items-center flex-col md:flex-row gap-0.5 md:gap-3.5">
                                    <span className="text-base md:text-lg font-bold leading-[160%] text-customGray">
                                      {list.name}
                                    </span>
                                    {/* <span className="hidden md:block w-[5px] h-[5px] rounded-[50%] bg-[#485868]"></span>
                                    <span
                                      className={`text-sm md:text-base font-medium leading-[160%] ${
                                        index == 0 &&
                                        formik.values.selectedServices1
                                          ?.length > 0
                                          ? "text-[#485868]"
                                          : index == 1 &&
                                            formik.values.selectedServices2
                                              ?.length > 0
                                          ? "text-[#485868]"
                                          : index == 2 &&
                                            formik.values.selectedServices3
                                              ?.length > 0
                                          ? "text-[#485868]"
                                          : "text-[#EF2121]"
                                      }`}
                                    >
                                      Services:{" "}
                                      {(index == 0
                                        ? formik.values.selectedServices1
                                            ?.length
                                        : index == 1
                                        ? formik.values.selectedServices2
                                            ?.length
                                        : formik.values.selectedServices3
                                            ?.length) || 0}
                                    </span> */}
                                  </div>
                                  <span className="ml-6 flex h-7 items-center">
                                    {open ? (
                                      <ChevronUpIcon
                                        onClick={() => {
                                          //   setSelectedProfessionServices(
                                          //     list.id
                                          //   );
                                        }}
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <ChevronDownIcon
                                        onClick={() => {
                                          //   setSelectedProfessionServices(
                                          //     list.id
                                          //   );
                                        }}
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </Disclosure.Button>
                                <Transition
                                  show={open}
                                  className="overflow-hidden"
                                  enter="transition transition-[max-height] duration-500 ease-in"
                                  enterFrom="transform max-h-0"
                                  enterTo="transform max-h-full"
                                  leave="transition transition-[max-height] duration-500 ease-out"
                                  leaveFrom="transform max-h-full"
                                  leaveTo="transform max-h-0"
                                >
                                  <Disclosure.Panel as="dd" className="mt-5">
                                    <form
                                      //   onSubmit={formik.handleSubmit}
                                      className=""
                                    >
                                      {results &&
                                        results[index]?.length > 0 &&
                                        formatServices(results[index])?.map(
                                          (service) => (
                                            <CreateOptions
                                              key={service.id}
                                              option={service}
                                              bgColor="#FFFFFF"
                                              onCheckboxChange={(
                                                id,
                                                isChecked
                                              ) =>
                                                handleCheckbox(
                                                  results[index].find(
                                                    (serv) => serv.id === id
                                                  ),
                                                  isChecked
                                                )
                                              }
                                              selectedOptions={
                                                selectedProfessionServices &&
                                                selectedProfessionServices[
                                                  list.id
                                                ]?.map((item) => item.id)
                                              }
                                            />
                                          )
                                        )}
                                    </form>
                                  </Disclosure.Panel>
                                </Transition>
                              </>
                            )}
                          </Disclosure>
                        </>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center flex-col md:flex-row py-4">
                  <Button
                    type="button"
                    title="back"
                    classes="h-[48px] md:h-[58px] flex items-center bg-[#F1F4F6] text-black rounded-[10px] font-black uppercase text-[#485868] tracking-[0.7px] w-full md:w-[160px] justify-center mb-[14px] md:mb-0"
                    onClick={handleBackButtonClick}
                    iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
                  />
                  <Button
                    type="button"
                    title="save"
                    classes={`h-[48px] md:h-[58px] bg-primary text-white rounded-[10px] font-black text-[16px] leading-[160%] uppercase tracking-[0.5px] w-full md:w-[160px] duration-500 hover:bg-[#026ed6] flex items-center justify-center max-w-full md:max-w-[143px] md:w-full ${
                      saveProfessionalServicesLoader
                        ? "pointer-events-none"
                        : "cursor-pointer"
                    }`}
                    onClick={handleSaveProfessionalServices}
                    buttonLoader={saveProfessionalServicesLoader}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AdminProfessionServices;
